/**
 * Icon Component
 * Wrapper for SVG icons with size variants
 */
import React from 'react';

export interface IconProps {
  name: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | number;
  className?: string;
  color?: string;
}

const icons: Record<string, React.FC<{ className?: string; style?: React.CSSProperties }>> = {
  instagram: ({ className, style }) => (
    <svg className={className} style={style} fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M7.75 2h8.5A5.75 5.75 0 0122 7.75v8.5A5.75 5.75 0 0116.25 22h-8.5A5.75 5.75 0 012 16.25v-8.5A5.75 5.75 0 017.75 2zm0 1.5A4.25 4.25 0 003.5 7.75v8.5a4.25 4.25 0 004.25 4.25h8.5a4.25 4.25 0 004.25-4.25v-8.5a4.25 4.25 0 00-4.25-4.25h-8.5zM12 7a5 5 0 110 10 5 5 0 010-10zm0 1.5a3.5 3.5 0 100 7 3.5 3.5 0 000-7zm5.25-2.5a1.125 1.125 0 110 2.25 1.125 1.125 0 010-2.25z"/>
    </svg>
  ),
  facebook: ({ className, style }) => (
    <svg className={className} style={style} fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  ),
  linkedin: ({ className, style }) => (
    <svg className={className} style={style} fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
  ),
  twitter: ({ className, style }) => (
    <svg className={className} style={style} fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>
  ),
  explore: ({ className, style }) => (
    <svg className={className} style={style} width="20" height="20" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <rect x="0.100006" y="10.1" width="9" height="9" rx="4.5" />
      <rect x="10.4" y="10.4" width="8.8" height="8.8" rx="1" />
      <path d="M7.7998 0C8.35209 0 8.7998 0.447715 8.7998 1V7.7998C8.7998 8.35209 8.35209 8.7998 7.7998 8.7998H1C0.447716 8.7998 0 8.35209 0 7.7998V1C3.46729e-07 0.447716 0.447715 0 1 0H7.7998ZM2.40039 2.40039V6.40039H6.40039V2.40039H2.40039Z" />
      <path fillRule="evenodd" clipRule="evenodd" d="M16.3556 1.14703C16.7462 0.756649 17.3792 0.756596 17.7697 1.14703L18.0529 1.43024C18.4431 1.82074 18.4432 2.45387 18.0529 2.8443L16.4972 4.39996L18.0529 5.95563C18.4431 6.34611 18.4432 6.97924 18.0529 7.36969L17.7697 7.65289C17.3793 8.04315 16.7461 8.04309 16.3556 7.65289L14.8 6.09723L13.2443 7.65289C12.8539 8.04317 12.2208 8.04305 11.8302 7.65289L11.547 7.36969C11.1566 6.97921 11.1567 6.34615 11.547 5.95563L13.1027 4.39996L11.547 2.8443C11.1566 2.45383 11.1567 1.82077 11.547 1.43024L11.8302 1.14703C12.2208 0.756695 12.8538 0.756574 13.2443 1.14703L14.8 2.7027L16.3556 1.14703Z" />
    </svg>
  ),
  benefits: ({ className, style }) => (
    <svg className={className} style={style} viewBox="0 0 34 42" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M16.6667 41.9818L1.66667 31.1865C1.14583 30.82 0.737847 30.3535 0.442708 29.7871C0.147569 29.2207 0 28.6209 0 27.9879V3.99826C0 2.89874 0.407986 1.95748 1.22396 1.17449C2.03993 0.391497 3.02083 0 4.16667 0H29.1667C30.3125 0 31.2934 0.391497 32.1094 1.17449C32.9253 1.95748 33.3333 2.89874 33.3333 3.99826V27.9879C33.3333 28.6209 33.1858 29.2207 32.8906 29.7871C32.5955 30.3535 32.1875 30.82 31.6667 31.1865L16.6667 41.9818ZM16.6667 36.984L29.1667 27.9879V3.99826H4.16667V27.9879L16.6667 36.984ZM14.4792 25.9887L26.25 14.6936L23.3333 11.7949L14.4792 20.2912L10.1042 16.093L7.08333 18.8918L14.4792 25.9887ZM16.6667 3.99826H4.16667H29.1667H16.6667Z"/>
    </svg>
  ),
  difference: ({ className, style }) => (
    <svg className={className} style={style} viewBox="0 0 46 46" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M20.4444 46V29.1333L8.56111 41.0806L4.91944 37.4389L16.8667 25.5556H0V20.4444H16.8667L4.91944 8.56111L8.56111 4.91944L20.4444 16.8667V0H25.5556V16.8667L37.4389 4.91944L41.0806 8.56111L29.1333 20.4444H46V25.5556H29.1333L41.0806 37.4389L37.4389 41.0806L25.5556 29.1333V46H20.4444Z"/>
    </svg>
  ),
  feature: ({ className, style }) => (
    <svg className={className} style={style} viewBox="0 0 48 45" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M24 20.4711C21.0667 20.4711 18.3333 19.9487 15.8 18.9038C13.2667 17.8589 11.0222 16.4195 9.06667 14.5857L13.8667 11.8349C14.9333 12.6878 16.0889 13.3915 17.3333 13.9459C18.5778 14.5004 19.9111 14.8842 21.3333 15.0974V1.53533L24 0L26.6667 1.53533V15.0974C28.0889 14.8842 29.4222 14.5004 30.6667 13.9459C31.9111 13.3915 33.0667 12.6878 34.1333 11.8349L38.9333 14.5857C36.9778 16.4195 34.7333 17.8589 32.2 18.9038C29.6667 19.9487 26.9333 20.4711 24 20.4711ZM20.7333 44.4607L15.8 41.9658C15.8889 41.582 15.9444 41.1981 15.9667 40.8143C15.9889 40.4305 16 40.0466 16 39.6628C16 38.6819 15.9111 37.7116 15.7333 36.7521C15.5556 35.7925 15.2889 34.8862 14.9333 34.0332L2.73333 41.0702L0 39.6628V36.6561L12.1333 29.6831C11.2444 28.7022 10.2222 27.8279 9.06667 27.0603C7.91111 26.2926 6.66667 25.6742 5.33333 25.2051V19.8314C9.95556 20.9829 13.7778 23.3819 16.8 27.0283C19.8222 30.6747 21.3333 34.8862 21.3333 39.6628C21.3333 40.5158 21.2889 41.3367 21.2 42.1257C21.1111 42.9147 20.9556 43.693 20.7333 44.4607ZM27.2667 44.4607C27.0444 43.693 26.8889 42.9147 26.8 42.1257C26.7111 41.3367 26.6667 40.5158 26.6667 39.6628C26.6667 34.8862 28.1778 30.6747 31.2 27.0283C34.2222 23.3819 38.0444 20.9829 42.6667 19.8314V25.2051C41.3333 25.6742 40.0889 26.2926 38.9333 27.0603C37.7778 27.8279 36.7556 28.7022 35.8667 29.6831L48 36.6561V39.6628L45.2667 41.0702L33.0667 34.0332C32.7111 34.8862 32.4444 35.7925 32.2667 36.7521C32.0889 37.7116 32 38.6819 32 39.6628C32 40.0466 32.0111 40.4305 32.0333 40.8143C32.0556 41.1981 32.1111 41.582 32.2 41.9658L27.2667 44.4607Z"/>
    </svg>
  ),
};

export const Icon = ({
  name,
  size = 'md',
  className = '',
  color,
}: IconProps) => {
  // Size classes for named sizes
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-12 h-12',
  };

  // Determine if size is a number or a string
  const sizeClass = typeof size === 'number'
    ? '' // Don't use size classes if numeric
    : sizeClasses[size];

  const sizeStyle = typeof size === 'number'
    ? { width: `${size}px`, height: `${size}px` }
    : {};

  const IconComponent = icons[name];

  if (!IconComponent) {
    return (
      <span
        className={`inline-flex ${sizeClass} ${color || ''} ${className}`}
        style={sizeStyle}
        data-icon={name}
      />
    );
  }

  return <IconComponent className={`${sizeClass} ${color || ''} ${className}`} style={sizeStyle} />;
};

export default Icon;
